/**
 * @fileoverview Admin Dashboard page component
 * Displays admin-specific dashboard with statistics, quick access buttons, and alerts
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS, ROUTES } from "dmsbasereact";
import Swal from "sweetalert2";

import { DashboardLayout } from "../../components/Dashboard";
import { PatientDetailModal } from "../../components/PatientDetailModal";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";

// MUI Icons
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import GroupIcon from "@mui/icons-material/Group";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SchoolIcon from "@mui/icons-material/School";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import MedicationIcon from "@mui/icons-material/Medication";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PersonIcon from "@mui/icons-material/Person";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { PatientStatusReason } from "../../constants/PatientStatusReason";

/**
 * AdminDashboard - Dashboard page for admin users
 * @returns {JSX.Element} The admin dashboard with stats, quick access, and alerts
 */
export const AdminDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        stats: null,
        alerts: null,
    });
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedPatientDetail, setSelectedPatientDetail] = useState(null);
    const [loadingPatientDetail, setLoadingPatientDetail] = useState(false);

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, alertsResponse] = await Promise.all([
                fetchData("/dashboard/admin/stats", "GET", true),
                fetchData("/dashboard/admin/alerts", "GET", true),
            ]);

            const newData = { stats: null, alerts: null };

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                newData.stats = statsResponse.data?.data;
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                newData.alerts = alertsResponse.data?.data;
            }

            setDashboardData(newData);
        } catch (err) {
            console.error("Error fetching admin dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending approvals alerts
     */
    const formatPendingApprovalsAlerts = () => {
        if (!dashboardData.alerts?.pending_approvals) return [];
        return dashboardData.alerts.pending_approvals.map((item) => ({
            id: item.institution_pk,
            title: item.name || item.institution_name,
            description: item.created_on ? new Date(item.created_on).toLocaleDateString('pt-BR') : intl.get("PendingApproval").d("Pending Approval"),
            institution_pk: item.institution_pk,
        }));
    };

    const formatInactivationAndActivationAlerts = () => {
        if (!dashboardData.alerts?.inactivation_history) return [];
        return dashboardData.alerts.inactivation_history.map(item => {
            const changeTypeLabel = item.change_type === "ACTIVATED"
                ? intl.get("Activated").d("Activated")
                : intl.get("Deactivated").d("Deactivated");

            const reasonText = item.reason
                ? ` - ${PatientStatusReason.getText(item.reason)}`
                : "";

            const dateText = item.updated_on
                ? new Date(item.updated_on).toLocaleDateString('pt-BR')
                : "";

            return {
                id: `${item.patient_pk}_${item.updated_on}`,
                title: item.patient_name,
                description: `${changeTypeLabel}${reasonText} (${dateText})`,
                patient_pk: item.patient_pk
            };
        });
    };

    /**
     * Format recent registrations alerts
     */
    const formatRecentRegistrationsAlerts = () => {
        if (!dashboardData.alerts?.recent_registrations) return [];
        return dashboardData.alerts.recent_registrations.map((item) => ({
            id: item.user_pk,
            title: item.user_name,
            description: item.created_on ? new Date(item.created_on).toLocaleDateString('pt-BR') : "",
            user_pk: item.user_pk,
        }));
    };

    /**
     * Handle click on pending approval alert
     */
    const handleApprovalClick = (item) => {
        if (item?.institution_pk) {
            navigate(APP_ROUTES.InstitutionApproval);
        }
    };

    /**
     * Handle click on recent registration alert
     */
    const handleRegistrationClick = (item) => {
        if (item?.user_pk) {
            navigate(APP_ROUTES.UserManagement);
        }
    };

    /**
     * Handle click on inactivation history alert
     */
    const handleAlertClick = useCallback(async (item) => {
        if (!item?.patient_pk) return;

        setLoadingPatientDetail(true);
        setDetailModalOpen(true);

        try {
            const response = await fetchData(`/patient/${item.patient_pk}/details`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setSelectedPatientDetail(response.data);
            } else {
                setDetailModalOpen(false);
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: intl.get("failed_load_patient_details").d("Failed to load patient details")
                });
            }
        } catch (error) {
            setDetailModalOpen(false);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: error.message
            });
        }

        setLoadingPatientDetail(false);
    }, []);

    /**
     * Handle closing the patient detail modal
     */
    const handleCloseDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedPatientDetail(null);
    }, []);

    /**
     * Handle saving patient changes
     */
    const handleSavePatient = useCallback(async (patientPk, payload) => {
        try {
            const response = await fetchData(`/patient/${patientPk}`, 'PUT', true, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    /**
     * Handle patient updated - refresh dashboard data
     */
    const handlePatientUpdated = useCallback(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Stats configuration - comprehensive view for admin
    const stats = [
        // Row 1: Users overview
        {
            title: intl.get("TotalUsers").d("Total Users"),
            value: dashboardData.stats?.total_users || 0,
            icon: PeopleIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.UserManagement),
        },
        {
            title: intl.get("TotalDoctors").d("Total Doctors"),
            value: dashboardData.stats?.total_doctors || 0,
            icon: LocalHospitalIcon,
            color: APP_COLORS.system.info,
        },
        {
            title: intl.get("TotalPharmacists").d("Total Pharmacists"),
            value: dashboardData.stats?.total_pharmacists || 0,
            icon: VaccinesIcon,
            color: APP_COLORS.system.success,
        },
        {
            title: intl.get("TotalPatients").d("Total Patients"),
            value: dashboardData.stats?.total_patients || 0,
            icon: PersonIcon,
            color: APP_COLORS.secondary.main,
        },
        {
            title: intl.get("InactivePatients").d("Inactive Patients"),
            value: dashboardData.stats?.inactive_patients || 0,
            icon: PeopleIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.ReportsPatients, { state: { initialFilters: { status: [2] } } })
        },
        // Row 2: Institutions
        {
            title: intl.get("TotalInstitutions").d("Total Institutions"),
            value: dashboardData.stats?.total_institutions || 0,
            icon: BusinessIcon,
            color: APP_COLORS.secondary.main,
            onClick: () => navigate(APP_ROUTES.InstitutionManagement),
        },
        {
            title: intl.get("PendingApprovals").d("Pending Approvals"),
            value: dashboardData.stats?.pending_institutions || 0,
            icon: FactCheckIcon,
            color: APP_COLORS.system.warning,
            onClick: () => navigate(APP_ROUTES.InstitutionApproval),
        },
        // Row 3: Prescriptions and Dispensations
        {
            title: intl.get("PrescriptionsThisMonth").d("Prescriptions This Month"),
            value: dashboardData.stats?.prescriptions_this_month || 0,
            icon: DescriptionIcon,
            color: APP_COLORS.system.info,
        },
        {
            title: intl.get("DispensationsThisMonth").d("Dispensations This Month"),
            value: dashboardData.stats?.dispensations_this_month || 0,
            icon: MedicationIcon,
            color: APP_COLORS.system.success,
        },
        // Row 4: Returns and Training
        {
            title: intl.get("PendingReturns").d("Pending Returns"),
            value: dashboardData.stats?.pending_returns || 0,
            icon: AssignmentReturnIcon,
            color: APP_COLORS.system.error,
        },
        {
            title: intl.get("TrainingCompleted").d("Training Completed"),
            value: dashboardData.stats?.training_completed || 0,
            icon: SchoolIcon,
            color: APP_COLORS.system.success,
            onClick: () => navigate(APP_ROUTES.CompletedTrainings),
        },
    ];

    // Quick access buttons configuration
    const quickAccess = [
        {
            label: intl.get("UserManagement").d("User Management"),
            icon: GroupIcon,
            route: APP_ROUTES.UserManagement,
            color: APP_COLORS.primary.main,
        },
        {
            label: intl.get("Institutions").d("Institutions"),
            icon: FactCheckIcon,
            route: APP_ROUTES.InstitutionApproval,
            color: APP_COLORS.secondary.main,
        },
        {
            label: intl.get("Training").d("Training"),
            icon: SchoolIcon,
            route: APP_ROUTES.TrainingCentral,
            color: APP_COLORS.system.info,
        },
        {
            label: intl.get("Analytics").d("Analytics"),
            icon: AnalyticsIcon,
            route: APP_ROUTES.DispensationInsights,
            color: APP_COLORS.system.success,
        },
        {
            label: intl.get("Traceability").d("Traceability"),
            icon: CompareArrowsIcon,
            route: APP_ROUTES.TraceabilitySap,
            color: APP_COLORS.system.warning,
        },
        {
            label: intl.get("Settings").d("Settings"),
            icon: SettingsIcon,
            route: ROUTES.SystemSettings,
            color: APP_COLORS.system.warning,
        },
    ];

    // Alerts configuration
    const alerts = [
        {
            title: intl.get("PendingApprovals").d("Pending Approvals"),
            items: formatPendingApprovalsAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPendingApprovals").d("No pending approvals"),
            onItemClick: handleApprovalClick,
        },
        {
            title: intl.get("InactivationAndActivationHistory").d("Inactivation and Activation History"),
            items: formatInactivationAndActivationAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoInactivationAndActivationHistory").d("No inactivation and activation history"),
            onItemClick: handleAlertClick
        },
        {
            title: intl.get("RecentRegistrations").d("Recent Registrations"),
            items: formatRecentRegistrationsAlerts(),
            severity: "info",
            emptyMessage: intl.get("NoRecentRegistrations").d("No recent registrations"),
            onItemClick: handleRegistrationClick,
        },
    ];

    return (
        <>
            <DashboardLayout
                welcomeName={dashboardData.stats?.admin_name}
                loading={loading}
                stats={stats}
                quickAccess={quickAccess}
                alerts={alerts}
            />
            <PatientDetailModal
                open={detailModalOpen}
                onClose={handleCloseDetailModal}
                patient={selectedPatientDetail}
                loading={loadingPatientDetail}
                canEdit={true}
                onSave={handleSavePatient}
                onPatientUpdated={handlePatientUpdated}
            />
        </>
    );
};
